import { useTranslations } from "@zapier/i18n";

import {
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
} from "@zapier/design-system";

import {
  getMessageFromNetworkError,
  useCancelApproval,
  useUserAccountRole,
  useZapApprovals,
} from "@zapier/client";
import { ApprovalType, useNotification } from "@zapier/utils";

import styles from "./CancelApprovalModal.module.css";

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  zapId?: string;
  type: ApprovalType | undefined;
}

interface ContentProps extends Props {
  onSubmit: () => void;
}

function CancelApprovalContent({
  isOpen,
  onSubmit,
  setIsOpen,
  type,
}: ContentProps) {
  const t = useTranslations("cancel-approval-modal");

  return (
    <ModalDialogRoot
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className={styles.modalContent}
    >
      <ModalDialogHeader textAlign="start">
        <Text color="GrayWarm10" type="Body4">
          {/* Include new ApprovalTypes in the `delete-request-title` shared.json */}
          {t("headings.delete-request-title", {
            type: type ?? "other",
          })}
        </Text>
      </ModalDialogHeader>
      <ModalDialogBody textAlign="start">
        <div className={styles.body}>
          <Text color="GrayWarm10" type="Body1">
            {t("messages.delete-request-warning")}
          </Text>
        </div>
      </ModalDialogBody>
      <ModalDialogFooter>
        <Button variant="secondary" onPress={() => setIsOpen(false)}>
          {t("buttons.cancel")}
        </Button>

        <Button variant="danger" onPress={onSubmit}>
          {t("buttons.delete-request")}
        </Button>
      </ModalDialogFooter>
    </ModalDialogRoot>
  );
}

export const CancelApprovalModal = ({
  isOpen,
  zapId,
  setIsOpen,
  type,
}: Props) => {
  const t = useTranslations("cancel-approval-modal");

  const { createNotification } = useNotification();
  const {
    pendingDeleteApproval,
    pendingEnableOrDisableApproval,
    pendingPublishApproval,
  } = useZapApprovals({ zapId });
  const { cancelApproval } = useCancelApproval();
  const { customuserId } = useUserAccountRole();

  // This callback needs to happen outside of the modal so that it can
  // still display a notification when the request completes after the modal has closed.
  const handleSubmit = async () => {
    if (!zapId) {
      return;
    }

    await cancelApproval(
      {
        zapId,
        userId: String(customuserId),
        type,
        id:
          Number(pendingDeleteApproval?.id) ||
          Number(pendingEnableOrDisableApproval?.id) ||
          Number(pendingPublishApproval?.id),
      },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>{t("notifications.request-cancelled-success")}</Text>
            ),
            type: "success",
          });
        },
        onError: (error: any) => {
          createNotification({
            title: t("errors.unable-to-cancel"),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
        onSettled: () => {
          // Close the modal after the request is completed,
          // so that notifications are displayed
          setIsOpen(false);
        },
      },
    );
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return null;
  }

  return (
    <CancelApprovalContent
      isOpen={isOpen}
      onSubmit={handleSubmit}
      setIsOpen={setIsOpen}
      type={type}
      zapId={zapId}
    />
  );
};
